import type { LucideIcon } from "lucide-react";
import { Coins, Zap, Crown, Sparkles } from "lucide-react";
import { getEffectiveCredits, isPromotionEnabled } from "@/lib/payments/client";
import { CREDIT_PACK_BASE_CONFIG } from "@/lib/payments/pack-config";
import { FREE_PREVIEW_LIMIT } from "@/lib/payments/free-preview-config";

// Credit pack IDs (matching server-side CREDIT_PACKS)
export type CreditPackId = "starter" | "pro" | "power" | "trial" | "mini" | "basic";
export type CreditPlanId = "free" | CreditPackId;

// Icon identifier type (for serialization between server and client)
export type IconName = "coins" | "zap" | "crown" | "sparkles";

// Icon mapping for client components
export const ICON_MAP: Record<IconName, LucideIcon> = {
  coins: Coins,
  zap: Zap,
  crown: Crown,
  sparkles: Sparkles,
};

// Serializable plan data (can be passed from server to client)
export type CreditPlanData = {
  id: CreditPlanId;
  name: string;
  /** Display price in USD (e.g. 7.99). Derived from cents in CREDIT_PACKS where applicable. */
  priceUsd: number;
  /** Number of credits included (for free we omit or set 0). */
  credits: number;
  description: string;
  estimatedGenerations?: string;
  valueBreakdown: string[];
  iconName: IconName;
  gradient: string;
  iconColor: string;
  borderColor: string;
  hoverBorder: string;
  badge?: string;
  popular?: boolean;
  /** Whether this plan currently has an active promotion (e.g. extra credits). */
  hasPromo?: boolean;
  /** Whether this is the free, built-in plan (no checkout). */
  isFree?: boolean;
  /** Features/benefits list shown in the card. */
  features: string[];
};

// Client-side plan with resolved icon component
export type CreditPlan = Omit<CreditPlanData, "iconName"> & {
  icon: LucideIcon;
};

const centsToUsd = (cents: number) => cents / 100;

// Base (non-promo) configuration for all plans, including free.
// This is the single source of truth for pricing & benefits.
export const BASE_CREDIT_PLANS_DATA: CreditPlanData[] = [
  {
    id: "free",
    name: "Free Preview",
    priceUsd: 0,
    credits: 0,
    description: `Try with ${FREE_PREVIEW_LIMIT} free preview${FREE_PREVIEW_LIMIT === 1 ? "" : "s"}`,
    estimatedGenerations: String(FREE_PREVIEW_LIMIT),
    valueBreakdown: [
      `${FREE_PREVIEW_LIMIT} free watermarked preview${FREE_PREVIEW_LIMIT === 1 ? "" : "s"}`,
      "Great to test the workflow",
      "No credit card required",
    ],
    iconName: "coins",
    gradient: "from-emerald-500/20 to-teal-500/20",
    iconColor: "text-emerald-400",
    borderColor: "border-emerald-500/20",
    hoverBorder: "hover:border-emerald-500/40",
    badge: "Included",
    popular: false,
    isFree: true,
    features: [
      `${FREE_PREVIEW_LIMIT} free preview${FREE_PREVIEW_LIMIT === 1 ? "" : "s"} to test the experience`,
      "No subscription or commitment",
      "Result is watermarked",
    ],
  },
  {
    id: "mini",
    name: "Mini Pack", // Will be overridden by shared pack config
    priceUsd: 1.99, // Will be overridden by shared pack config
    credits: 3, // Will be overridden by shared pack config
    description: "A small pack to try a few wheel options",
    estimatedGenerations: "3",
    valueBreakdown: [
      "3 HD previews",
    ],
    iconName: "coins",
    gradient: "from-amber-500/20 to-orange-500/20",
    iconColor: "text-amber-400",
    borderColor: "border-amber-500/20",
    hoverBorder: "hover:border-amber-500/40",
    badge: "Try it",
    popular: false,
    features: [
      "Access to full catalog (100+ wheels)",
      "Upload custom wheels",
      "Credits never expire",
      "Download all images without watermarks",
      "Top up anytime",
    ],
  },
  {
    id: "basic",
    name: "Basic", // Will be overridden by server-side CREDIT_PACKS
    priceUsd: 4.99, // Will be overridden by server-side CREDIT_PACKS
    credits: 30, // Will be overridden by server-side CREDIT_PACKS
    description: "A flexible starting pack for new builds",
    estimatedGenerations: "30",
    valueBreakdown: [
      "30 HD previews",
    ],
    iconName: "coins",
    gradient: "from-emerald-500/20 to-teal-500/20",
    iconColor: "text-emerald-400",
    borderColor: "border-emerald-500/20",
    hoverBorder: "hover:border-emerald-500/40",
    badge: "Start Here",
    popular: true,
    features: [
      "Access to full catalog (100+ wheels)",
      "Upload custom wheels",
      "Credits never expire",
      "Download all images without watermarks",
      "Top up anytime",
    ],
  },
  {
    id: "starter",
    name: "Starter Pack", // Will be overridden by server-side CREDIT_PACKS
    priceUsd: 9.99, // Will be overridden by server-side CREDIT_PACKS
    credits: 75, // Will be overridden by server-side CREDIT_PACKS
    description: "The best fit for enthusiasts and repeat comparisons",
    estimatedGenerations: "75",
    valueBreakdown: [
      "75 HD previews",
    ],
    iconName: "zap",
    gradient: "from-emerald-500/20 to-teal-500/20",
    iconColor: "text-emerald-400",
    borderColor: "border-emerald-500/20",
    hoverBorder: "hover:border-emerald-500/40",
    badge: "More Previews",
    popular: false,
    features: [
      "Access to full catalog (100+ wheels)",
      "Upload custom wheels",
      "Credits never expire",
      "Download all images without watermarks",
      "Top up anytime",
    ],
  },
  {
    id: "pro",
    name: "Pro Pack", // Will be overridden by server-side CREDIT_PACKS
    priceUsd: 19.99, // Will be overridden by server-side CREDIT_PACKS
    credits: 175, // Will be overridden by server-side CREDIT_PACKS
    description: "For larger builds and repeat users",
    estimatedGenerations: "175",
    valueBreakdown: [
      "175 HD previews",
    ],
    iconName: "crown",
    gradient: "from-blue-500/30 to-violet-500/30",
    iconColor: "text-blue-400",
    borderColor: "border-blue-500/40",
    hoverBorder: "hover:border-blue-500/60",
    badge: "Best value",
    popular: false,
    features: [
      "Access to full catalog (100+ wheels)",
      "Upload custom wheels",
      "Credits never expire",
      "Download all images without watermarks",
      "Top up anytime",
    ],
  },
  {
    id: "power",
    name: "Power Pack", // Will be overridden by server-side CREDIT_PACKS
    priceUsd: 39.99, // Will be overridden by server-side CREDIT_PACKS
    credits: 400, // Will be overridden by server-side CREDIT_PACKS
    description: "High-volume previews for dealers and power users",
    estimatedGenerations: "400",
    valueBreakdown: [
      "400 HD previews",
    ],
    iconName: "sparkles",
    gradient: "from-amber-500/20 to-orange-500/20",
    iconColor: "text-amber-400",
    borderColor: "border-amber-500/20",
    hoverBorder: "hover:border-amber-500/40",
    badge: "For dealers",
    popular: false,
    features: [
      "Access to full catalog (100+ wheels)",
      "Upload custom wheels",
      "Credits never expire",
      "Download all images without watermarks",
      "Top up anytime",
    ],
  },
];

export type GetDisplayPlansOptions = {
  /** Whether to include the free preview card. */
  includeFree?: boolean;
  /** Whether the Starter promo is enabled. If omitted, inferred from CREDIT_PACKS. */
  isStarterPromoEnabled?: boolean;
  /** Effective credits for Starter when promo is enabled. If omitted, derived via getEffectiveCredits. */
  starterPromoCredits?: number;
};

/**
 * Client-side function: Converts serializable plan data to client plan with resolved icons.
 */
export function resolveCreditPlans(plansData: CreditPlanData[]): CreditPlan[] {
  return plansData.map((plan) => ({
    ...plan,
    icon: ICON_MAP[plan.iconName],
  }));
}

/**
 * Client-side function: Returns credit plans using client-safe utilities.
 * Falls back to this if server-side data is not available.
 */
export function getDisplayCreditPlansClient(
  options: GetDisplayPlansOptions = {}
): CreditPlan[] {

  const {
    includeFree = true,
    isStarterPromoEnabled: isPromoEnabledOverride,
    starterPromoCredits: starterPromoOverride,
  } = options;

  const isStarterPromo =
    typeof isPromoEnabledOverride === "boolean"
      ? isPromoEnabledOverride
      : isPromotionEnabled("starter");

  const starterPromoCredits =
    typeof starterPromoOverride === "number"
      ? starterPromoOverride
      : getEffectiveCredits("starter");

  // Sourced from the shared pack config so this never drifts from the
  // server-side pricing in `@/lib/payments`.
  const CLIENT_PACKS = CREDIT_PACK_BASE_CONFIG;
  const hasStarterBonus =
    isStarterPromo && starterPromoCredits > CLIENT_PACKS.starter.credits;

  return BASE_CREDIT_PLANS_DATA.filter((plan) => {
    if (plan.isFree) {
      return includeFree;
    }
    return true;
  })
    .map((plan) => {
      if (plan.id === "mini") {
        return {
          ...plan,
          name: CLIENT_PACKS.mini.name,
          priceUsd: centsToUsd(CLIENT_PACKS.mini.price),
          credits: CLIENT_PACKS.mini.credits,
          hasPromo: false,
          icon: ICON_MAP[plan.iconName],
        };
      }

      if (plan.id === "basic") {
        return {
          ...plan,
          name: CLIENT_PACKS.basic.name,
          priceUsd: centsToUsd(CLIENT_PACKS.basic.price),
          credits: CLIENT_PACKS.basic.credits,
          hasPromo: false,
          icon: ICON_MAP[plan.iconName],
        };
      }

      if (plan.id === "starter") {
        const basePlan = {
          ...plan,
          name: CLIENT_PACKS.starter.name,
          priceUsd: centsToUsd(CLIENT_PACKS.starter.price),
          credits: hasStarterBonus ? starterPromoCredits : CLIENT_PACKS.starter.credits,
        };

        if (hasStarterBonus) {
          return {
            ...basePlan,
            estimatedGenerations: String(starterPromoCredits),
            valueBreakdown: [
              `${starterPromoCredits} HD previews`,
            ],
            badge: "Limited Time",
            hasPromo: true,
            icon: ICON_MAP[plan.iconName],
          };
        }
        return {
          ...basePlan,
          hasPromo: false,
          icon: ICON_MAP[plan.iconName],
        };
      }

      if (plan.id === "pro") {
        return {
          ...plan,
          name: CLIENT_PACKS.pro.name,
          priceUsd: centsToUsd(CLIENT_PACKS.pro.price),
          credits: CLIENT_PACKS.pro.credits,
          hasPromo: false,
          icon: ICON_MAP[plan.iconName],
        };
      }

      if (plan.id === "power") {
        return {
          ...plan,
          name: CLIENT_PACKS.power.name,
          priceUsd: centsToUsd(CLIENT_PACKS.power.price),
          credits: CLIENT_PACKS.power.credits,
          hasPromo: false,
          icon: ICON_MAP[plan.iconName],
        };
      }

      return {
        ...plan,
        hasPromo: false,
        icon: ICON_MAP[plan.iconName],
      };
    });
}
